import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Grid, Typography, Button, Divider, IconButton, TextField, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControl, InputLabel,
  Select, MenuItem, Chip, Checkbox, FormControlLabel, FormGroup, Switch,
  Accordion, AccordionSummary, AccordionDetails, Snackbar, Alert, Tooltip, Stack,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ThemeProvider } from "@mui/material/styles";
import darkTheme from "../../style/theme";
import DashboardBreadcrumbs from "../../components/DashboardBreadcrumbs";
import { Icon as OpusIcon, Toggle as OpusToggle, Button as OpusButton, Badge as OpusBadge, RoleBadge, Modal as OpusModal, Field as OpusField, TextInput as OpusTextInput, Select as OpusSelect, IconButton as OpusIconButton } from "../../components/common/opus";
import StatusChip from "../../components/common/StatusChip";
import apiService from "../../services/api.service";

const EMPTY_EDITOR = { open: false, mode: "create", id: null, name: "", description: "", status: "active", selected: [], wildcard: false };

const RolesManagement = () => {
  const [roles, setRoles] = useState([]);
  const [groups, setGroups] = useState([]); // [{domain,label,permissions[]}]
  const [knownPerms, setKnownPerms] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(EMPTY_EDITOR);
  const [openGroups, setOpenGroups] = useState({}); // OPUS-913: collapsible permission groups in the editor
  const [snack, setSnack] = useState({ open: false, severity: "success", msg: "" });

  const notify = (severity, msg) => setSnack({ open: true, severity, msg });

  const loadRoles = useCallback(async () => {
    try {
      const data = await apiService.getRoles();
      setRoles(Array.isArray(data) ? data : data?.data || []);
    } catch (e) {
      notify("error", `Failed to load roles: ${e.message}`);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [rolesData, catalog] = await Promise.all([
          apiService.getRoles(),
          apiService.getPermissionCatalog(),
        ]);
        setRoles(Array.isArray(rolesData) ? rolesData : rolesData?.data || []);
        const cat = catalog?.data || catalog || {};
        setGroups(cat.groups || []);
        setKnownPerms(new Set(cat.permissions || []));
      } catch (e) {
        notify("error", `Failed to load roles/catalog: ${e.message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ---- editor open/close ----
  const openCreate = () =>
    setEditor({ ...EMPTY_EDITOR, open: true, mode: "create" });

  const openEdit = (role) => {
    const perms = Array.isArray(role.permissions) ? role.permissions : [];
    const wildcard = perms.includes("*");
    setEditor({
      open: true,
      mode: "edit",
      id: role._id,
      name: role.name || "",
      description: role.description || "",
      status: role.status || "active",
      // only catalog-known perms are editable as checkboxes
      selected: perms.filter((p) => p !== "*" && knownPerms.has(p)),
      wildcard,
    });
  };

  const closeEditor = () => setEditor(EMPTY_EDITOR);

  // ---- permission toggles ----
  const togglePerm = (perm) =>
    setEditor((e) => ({
      ...e,
      selected: e.selected.includes(perm)
        ? e.selected.filter((p) => p !== perm)
        : [...e.selected, perm],
    }));

  const toggleGroup = (groupPerms, checked) =>
    setEditor((e) => {
      const set = new Set(e.selected);
      groupPerms.forEach((p) => (checked ? set.add(p) : set.delete(p)));
      return { ...e, selected: Array.from(set) };
    });

  const groupState = (groupPerms) => {
    const inSel = groupPerms.filter((p) => editor.selected.includes(p)).length;
    return { all: inSel === groupPerms.length && groupPerms.length > 0, some: inSel > 0 && inSel < groupPerms.length };
  };

  // ---- save / status ----
  const handleSave = async () => {
    if (editor.mode === "create" && !editor.name.trim()) {
      notify("warning", "Role name is required.");
      return;
    }
    const permissions = editor.wildcard ? ["*"] : editor.selected;
    setSaving(true);
    try {
      if (editor.mode === "create") {
        await apiService.createRole({
          name: editor.name.trim(),
          description: editor.description,
          permissions,
          status: editor.status,
        });
        notify("success", `Role "${editor.name.trim()}" created.`);
      } else {
        await apiService.updateRole(editor.id, {
          description: editor.description,
          permissions,
          status: editor.status,
        });
        notify("success", `Role "${editor.name}" updated.`);
      }
      closeEditor();
      await loadRoles();
    } catch (e) {
      notify("error", `Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (role) => {
    const next = role.status === "active" ? "inactive" : "active";
    try {
      await apiService.updateRole(role._id, { status: next });
      notify("success", `Role "${role.name}" ${next === "active" ? "activated" : "deactivated"}.`);
      await loadRoles();
    } catch (e) {
      notify("error", `Status change failed: ${e.message}`);
    }
  };

  const columns = [
    { field: "name", headerName: "Role", flex: 1, minWidth: 140, renderCell: (params) => <RoleBadge role={params.row.name} /> },
    { field: "description", headerName: "Description", flex: 1.4, minWidth: 180, renderCell: (params) => <span style={{ color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{params.row.description || "—"}</span> },
    {
      field: "permissions",
      headerName: "Permissions",
      flex: 0.8,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => {
        const p = Array.isArray(params.row.permissions) ? params.row.permissions : [];
        return p.includes("*")
          ? <OpusBadge tone="green" icon="shield">All (*)</OpusBadge>
          : <OpusBadge tone="neutral">{`${p.length} permission${p.length === 1 ? "" : "s"}`}</OpusBadge>;
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.6,
      minWidth: 110,
      renderCell: (params) => <StatusChip status={params.row.status} />,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Edit permissions">
            <IconButton size="small" color="info" onClick={() => openEdit(params.row)}>
              <OpusIcon name="edit" size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.status === "active" ? "Deactivate" : "Activate"}>
            <OpusToggle
              checked={params.row.status === "active"}
              onChange={() => toggleStatus(params.row)}
            />
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const selectedCount = editor.wildcard ? "all" : editor.selected.length;

  return (
    <ThemeProvider theme={darkTheme}>
      <Grid container spacing={1} sx={{ height: "100vh" }}>
        <Grid size={12}>
          <DashboardBreadcrumbs
            rightAction={
              <OpusButton variant="primary" icon="plus" onClick={openCreate}>Add Role</OpusButton>
            }
          />
        </Grid>

        <Grid size={12} sx={{ height: "100%" }}>
          <Box
            sx={{
              height: "calc(100% - 67px)",
              width: "100%",
              p: 2,
              background: "var(--surface-1)",
              border: "1px solid var(--border)",
              borderRadius: "var(--r-lg)",
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define what each role can do. Permissions are enforced by the backend; UI gating follows.
            </Typography>

            <Box sx={{ height: "calc(100% - 44px)", width: "100%" }}>
              <DataGrid
                rows={roles}
                columns={columns}
                loading={loading}
                getRowId={(row) => row._id}
                rowHeight={52}
                columnHeaderHeight={44}
                disableColumnMenu
                disableRowSelectionOnClick
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                pageSizeOptions={[10, 25, 50]}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Role editor (create + edit) — OPUS-913 design RoleEditorModal */}
      <OpusModal
        open={editor.open}
        onClose={closeEditor}
        icon="shield"
        width={620}
        title={editor.mode === "create" ? "Add New Role" : `Edit Role — ${editor.name}`}
        footer={<>
          <OpusButton variant="ghost" onClick={closeEditor} disabled={saving}>Cancel</OpusButton>
          <OpusButton variant="primary" icon={editor.mode === "create" ? "plus" : "check"} onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : editor.mode === "create" ? "Create Role" : "Save Changes"}
          </OpusButton>
        </>}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 16 }}>
            <OpusField label="Role name" required helper={editor.mode === "edit" ? "Role name can't be changed" : "e.g. designer"}>
              <OpusTextInput value={editor.name} onChange={(v) => setEditor((s) => ({ ...s, name: v }))} placeholder="designer" disabled={editor.mode === "edit"} icon="shield" />
            </OpusField>
            <OpusField label="Status">
              <OpusSelect value={editor.status} onChange={(v) => setEditor((s) => ({ ...s, status: v }))} options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} />
            </OpusField>
          </div>
          <OpusField label="Description">
            <OpusTextInput value={editor.description} onChange={(v) => setEditor((s) => ({ ...s, description: v }))} placeholder="What this role can do" />
          </OpusField>

          <div style={{ height: 1, background: "var(--border)" }} />

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>Permissions <span style={{ color: "var(--text-dim)", fontWeight: 400 }}>({selectedCount} selected)</span></span>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-muted)", cursor: "pointer" }}>Grant all (*) <OpusToggle checked={editor.wildcard} onChange={(v) => setEditor((s) => ({ ...s, wildcard: v }))} label="Grant all" /></label>
          </div>

          {editor.wildcard ? (
            <div style={{ display: "flex", gap: 10, padding: "12px 14px", background: "rgba(88,196,255,0.07)", border: "1px solid rgba(88,196,255,0.25)", borderRadius: "var(--r-md)", fontSize: 13, color: "var(--text-muted)" }}>
              <OpusIcon name="info" size={16} style={{ color: "var(--blue)", flexShrink: 0 }} />
              <span>This role is granted the <strong style={{ color: "var(--text)" }}>all-permissions wildcard (*)</strong> and bypasses every permission check. Turn off “Grant all (*)” to assign specific permissions.</span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {groups.map((g) => {
                const gs = groupState(g.permissions);
                const inSel = g.permissions.filter((p) => editor.selected.includes(p)).length;
                const isOpen = openGroups[g.domain] ?? true;
                return (
                  <div key={g.domain} style={{ border: "1px solid var(--border)", borderRadius: "var(--r-md)", overflow: "hidden" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 13px", background: "var(--surface-2)" }}>
                      <button type="button" onClick={() => toggleGroup(g.permissions, !gs.all)} aria-label="Toggle group"
                        style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${gs.all || gs.some ? "var(--green)" : "var(--border-strong)"}`, background: gs.all ? "var(--green)" : gs.some ? "rgba(69,255,167,0.3)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, padding: 0 }}>
                        {gs.all && <OpusIcon name="check" size={12} style={{ color: "#0a0a0a" }} />}
                        {gs.some && !gs.all && <span style={{ width: 8, height: 2, background: "var(--green)" }} />}
                      </button>
                      <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>{g.label}</span>
                      <OpusBadge tone="neutral">{inSel}/{g.permissions.length}</OpusBadge>
                      <OpusIconButton icon="chevronDown" size={15} title={isOpen ? "Collapse" : "Expand"} onClick={() => setOpenGroups((o) => ({ ...o, [g.domain]: !(o[g.domain] ?? true) }))} style={{ transform: isOpen ? "none" : "rotate(-90deg)" }} />
                    </div>
                    {isOpen && (
                      <div style={{ padding: "10px 13px 12px 40px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
                        {g.permissions.map((p) => {
                          const on = editor.selected.includes(p);
                          return (
                            <label key={p} style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer" }}>
                              <span onClick={() => togglePerm(p)} style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${on ? "var(--green)" : "var(--border-strong)"}`, background: on ? "var(--green)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                {on && <OpusIcon name="check" size={11} style={{ color: "#0a0a0a" }} />}
                              </span>
                              <span style={{ fontSize: 12.5, fontFamily: "var(--font-mono)", color: "var(--text-muted)" }}>{p}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </OpusModal>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default RolesManagement;
